import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { on, off } from '../utils/events';
import { useApi } from './ApiProvider';
import { onAuthStateChange } from '../firebase';
import tokenManager from '../utils/TokenManager';

const UserContext = createContext();

function UserProvider({ children }) {
  const [user, setUser] = useState();
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const api = useApi();

  const clearUser = React.useCallback(() => {
    setUser(null);
    setFirebaseUser(null);
  }, []);

  useEffect(() => {
    on('logout', clearUser);
    return () => {
      off('logout', clearUser);
    };
  }, [clearUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (currentFirebaseUser) => {
      try {
        if (currentFirebaseUser) {
          setFirebaseUser(currentFirebaseUser);
          await handleFirebaseUser(currentFirebaseUser);
          return;
        }

        setFirebaseUser(null);
        if (api.isAuthenticated()) {
          const response = await api.get('/me');
          setUser(response.ok ? response.body : null);
        } else {
          setUser(null);
        }
      } finally {
        setInitialized(true);
      }
    });

    return () => unsubscribe();
  }, []);

  async function createFirebaseUser(currentFirebaseUser) {
    const userInfo = {
      firebase_uid: currentFirebaseUser.uid,
      username: currentFirebaseUser.email,
      email: currentFirebaseUser.email,
      about_me: currentFirebaseUser.displayName || '',
    };

    try {
      const signupResult = await api.post('/users', userInfo);
      if (signupResult.ok) {
        setUser(signupResult.body);
      } else {
        console.error(
          'Error creating/updating user:',
          signupResult.body?.errors?.json
        );
      }
    } catch (error) {
      console.error('Error in createFirebaseUser:', error);
    }
  }

  function getProviderData(currentFirebaseUser) {
    if (currentFirebaseUser?.providerData?.length > 0) {
      return currentFirebaseUser.providerData[0].providerId.replace('.com', '');
    }
    return null;
  }

  async function handleFirebaseUser(currentFirebaseUser, hasRetried = false) {
    const token = await currentFirebaseUser.getIdToken();
    localStorage.setItem('accessToken', token);

    const existingUserResponse = await api.get(
      `/users/${currentFirebaseUser.email}`
    );

    if (existingUserResponse.ok) {
      const existingUser = existingUserResponse.body;
      if (existingUser.firebase_uid !== currentFirebaseUser.uid) {
        const updateUserResponse = await api.put('/users/auth-id', {
          id: String(existingUser.id),
          firebase_uid: currentFirebaseUser.uid,
          auth_provider: getProviderData(currentFirebaseUser),
        });
        if (updateUserResponse.ok) {
          setUser(updateUserResponse.body);
        } else {
          setUser(null);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      } else {
        setUser(existingUser);
      }
    } else if (existingUserResponse.status === 404) {
      await createFirebaseUser(currentFirebaseUser);
    } else if (
      existingUserResponse.status === 401 ||
      existingUserResponse.status === 403
    ) {
      if (!hasRetried) {
        try {
          await tokenManager.refreshToken({ force: true });
          await handleFirebaseUser(currentFirebaseUser, true);
          return;
        } catch (error) {
          console.error('Firebase token refresh failed:', error);
        }
      }

      setUser(null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  const setUserWithLogging = React.useCallback((newUser) => {
    setUser(newUser);
  }, []);

  const logout = async () => {
    await api.logout();
    setUser(null);
    setFirebaseUser(null);
  };

  return (
    <UserContext.Provider
      value={{
        user,
        firebaseUser,
        initialized,
        setUser: setUserWithLogging,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  return useContext(UserContext);
}

UserProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default UserProvider;
